import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Trophy, Calendar, Users, MessageSquare, 
  BarChart2, Star, Share2, MoreHorizontal, ThumbsUp,
  Target
} from 'lucide-react';
import { 
  getLeagueStandings, 
  getLeagueRound,
  getTopPlayerStats 
} from '../services/api';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    liveNow: 'AO VIVO',
    following: 'Seguindo',
    follow: 'Seguir',
    followers: 'seguidores',
    postsToday: 'posts hoje',
    friendsFollowing: 'Amigos que seguem',
    share: 'Compartilhar',
    g4Title: 'G4 Atual',
    viewAll: 'Ver tudo',
    currentRound: 'Rodada Atual',
    viewTable: 'Ver tabela',
    scheduled: 'Agendado',
    topScorers: 'Artilheiros',
    goals: 'gols',
    noStandings: 'Nenhum dado de classificação disponível',
    colPos: 'Pos', colTeam: 'Equipe', colP: 'J', colW: 'V', colD: 'E', colL: 'D', colGD: 'SG', colPts: 'PTS',
    breadcrumb: 'Competições',
    tabs: { feed: 'Discussão', standings: 'Classificação', matches: 'Jogos', stats: 'Estatísticas' },
    calendarTitle: 'Calendário Completo',
    calendarSubtitle: 'Visualize todos os jogos da temporada',
    statsTitle: 'Estatísticas Detalhadas',
    statsSubtitle: 'Análises aprofundadas da temporada',
    bolaoTitle: 'Participe do Bolão! 🏆',
    bolaoText: 'Acerte os placares da rodada e ganhe medalhas no seu perfil.',
    bolaoBtn: 'Dar Palpite',
    dateLocale: 'pt-BR',
  },
  en: {
    liveNow: 'LIVE',
    following: 'Following',
    follow: 'Follow',
    followers: 'followers',
    postsToday: 'posts today',
    friendsFollowing: 'Friends following',
    share: 'Share',
    g4Title: 'Top 4',
    viewAll: 'View all',
    currentRound: 'Current Round',
    viewTable: 'View table',
    scheduled: 'Scheduled',
    topScorers: 'Top Scorers',
    goals: 'goals',
    noStandings: 'No standings data available',
    colPos: 'Pos', colTeam: 'Team', colP: 'P', colW: 'W', colD: 'D', colL: 'L', colGD: 'GD', colPts: 'PTS',
    breadcrumb: 'Competitions',
    tabs: { feed: 'Discussion', standings: 'Standings', matches: 'Matches', stats: 'Statistics' },
    calendarTitle: 'Full Calendar',
    calendarSubtitle: 'View all matches of the season',
    statsTitle: 'Detailed Statistics',
    statsSubtitle: 'In-depth season analysis',
    bolaoTitle: 'Join the Predictor! 🏆',
    bolaoText: 'Predict the round scores and earn badges on your profile.',
    bolaoBtn: 'Make Prediction',
    dateLocale: 'en-US',
  }
};

// === CORES & TEMA ===
const BG_SOCIAL = '#f3f2ef';

// === CONFIGURAÇÃO DAS LIGAS ===
const MAIN_LEAGUES = [
  { id: 1, name: 'Brasileirão Série A', logo: 'https://media.api-sports.io/football/leagues/71.png', apiId: 71 },
  { id: 2, name: 'Brasileirão Série B', logo: 'https://media.api-sports.io/football/leagues/72.png', apiId: 72 },
  { id: 13, name: 'La Liga', logo: 'https://media.api-sports.io/football/leagues/140.png', apiId: 140 },
  { id: 16, name: 'Premier League', logo: 'https://media.api-sports.io/football/leagues/39.png', apiId: 39 },
  { id: 20, name: 'Major League Soccer', logo: 'https://media.api-sports.io/football/leagues/253.png', apiId: 253 },
  { id: 21, name: 'Serie A', logo: 'https://media.api-sports.io/football/leagues/135.png', apiId: 135 },
  { id: 22, name: 'Bundesliga', logo: 'https://media.api-sports.io/football/leagues/78.png', apiId: 78 },
  { id: 23, name: 'Ligue 1', logo: 'https://highlightly.net/soccer/images/leagues/52695.png', apiId: 52695 },
  { id: 24, name: 'Pro League', logo: 'https://highlightly.net/soccer/images/leagues/262041.png', apiId: 262041 },
  { id: 25, name: 'Primeira Liga', logo: 'https://highlightly.net/soccer/images/leagues/80778.png', apiId: 80778 },
  { id: 26, name: 'Liga MX', logo: 'https://highlightly.net/soccer/images/leagues/223746.png', apiId: 223746 }
];

// === MOCK DE DADOS SOCIAIS (dinâmico baseado no ID) ===
const getLeagueMeta = (leagueId) => {
  const covers = [
    "https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1522778119026-d647f0565c6a?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?q=80&w=1200&auto=format&fit=crop"
  ];
  
  const followersBase = [2400000, 5100000, 3800000, 4200000, 1500000, 2900000, 3100000, 2700000, 1200000, 900000, 1100000];
  const postsBase = [340, 890, 620, 750, 280, 490, 510, 430, 210, 150, 180];
  
  const id = parseInt(leagueId);
  const index = (id - 1) % covers.length;
  
  return {
    cover: covers[index],
    followers: followersBase[index] || 1000000,
    postsToday: postsBase[index] || 200
  };
};

// === COMPONENTE 1: HEADER DE COMUNIDADE ===
const LeagueCommunityHeader = ({ league, season, meta }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  const formatFollowers = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6 group">
      {/* Capa */}
      <div className="h-48 md:h-64 relative overflow-hidden bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10" />
        <img 
          src={meta.cover} 
          alt="Capa" 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
        />
        
        {/* Badge ao vivo */}
        <div className="absolute top-4 left-4 z-20 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 animate-pulse">
          <div className="w-2 h-2 bg-white rounded-full" />
          {t.liveNow}
        </div>
        
        {/* Ações de Capa */}
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <button className="p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-colors">
            <Share2 size={20} />
          </button>
        </div>
      </div>

      {/* Info Principal */}
      <div className="px-6 pb-6 relative">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-end -mt-12 z-20 relative">
          
          {/* Logo Flutuante */}
          <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-2xl shadow-lg p-3 border-4 border-white">
            <img src={league.logo} alt={league.name} className="w-full h-full object-contain" />
          </div>

          {/* Textos */}
          <div className="flex-1 mb-2">
            <h1 className="text-3xl font-extrabold text-gray-900 leading-none mb-1">
              {league.name}
            </h1>
            <p className="text-gray-500 text-sm font-medium flex items-center gap-2">
              <span style={{ color: BRAND_GREEN }} className="font-bold">Temporada {season}</span> 
              <span>•</span> 
              <span>{league.country || 'Internacional'}</span>
            </p>
          </div>

          {/* Ações Principais */}
          <div className="flex gap-3 w-full md:w-auto mb-2">
            <button 
              onClick={() => setIsFollowing(!isFollowing)}
              className={`flex-1 md:flex-none px-6 py-2.5 rounded-lg font-bold transition-colors shadow-sm flex items-center justify-center gap-2 ${
                isFollowing 
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                  : 'text-white hover:opacity-90'
              }`}
              style={isFollowing ? {} : { backgroundColor: BRAND_GREEN }}
            >
              <Star size={18} fill={isFollowing ? BRAND_GOLD : 'none'} className={isFollowing ? 'text-yellow-500' : ''} />
              {isFollowing ? t.following : t.follow}
            </button>
            <button className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors">
              <MoreHorizontal size={20} />
            </button>
          </div>
        </div>

        {/* Stats Sociais */}
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-1.5 text-gray-600">
            <Users size={16} style={{ color: BRAND_GREEN }} />
            <span className="font-bold text-gray-900">{formatFollowers(meta.followers)}</span> {t.followers}
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <MessageSquare size={16} style={{ color: BRAND_GOLD }} />
            <span className="font-bold text-gray-900">{meta.postsToday}</span> {t.postsToday}
          </div>
          <div className="flex items-center gap-1.5 text-gray-600 ml-auto">
            <span className="flex -space-x-2 mr-2">
              {[1,2,3].map(i => (
                <div key={i} className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white"></div>
              ))}
            </span>
            <span className="text-xs">{t.friendsFollowing}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE 2: POST DO FEED ===
const LeaguePost = ({ author, time, content, likes, comments, tag, avatar }) => {
  const [liked, setLiked] = useState(false);
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer mb-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
            <img 
              src={avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=064e3b&color=fff`} 
              alt={author} 
              className="w-full h-full object-cover" 
            />
          </div>
          <div>
            <h4 className="font-bold text-sm text-gray-900">{author}</h4>
            <p className="text-xs text-gray-500">
              {time} • <span style={{ color: BRAND_GREEN }} className="font-medium">{tag}</span>
            </p>
          </div>
        </div>
      </div>
      <p className="text-gray-800 text-sm leading-relaxed mb-3">{content}</p>
      <div className="flex items-center gap-6 pt-3 border-t border-gray-50">
        <button 
          onClick={() => setLiked(!liked)}
          className={`flex items-center gap-1.5 text-xs font-bold transition-colors ${
            liked ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'
          }`}
        >
          <ThumbsUp size={16} fill={liked ? 'currentColor' : 'none'} /> 
          {likes + (liked ? 1 : 0)}
        </button>
        <button className="flex items-center gap-1.5 text-xs font-bold text-gray-500 transition-colors" 
                style={{ '&:hover': { color: BRAND_GREEN } }}>
          <MessageSquare size={16} /> {comments}
        </button>
        <button className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors ml-auto">
          <Share2 size={16} /> {t.share}
        </button>
      </div>
    </div>
  );
};

// === COMPONENTE 3: WIDGET CLASSIFICAÇÃO MINI ===
const MiniStandingsWidget = ({ standings, leagueId }) => {
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  if (!standings || standings.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
        <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
          <Trophy size={16} style={{ color: BRAND_GOLD }} />
          {t.g4Title}
        </h3>
        <button 
          onClick={() => navigate(`#standings`)}
          className="text-xs font-bold hover:underline"
          style={{ color: BRAND_GREEN }}
        >
          {t.viewAll}
        </button>
      </div>
      <div className="divide-y divide-gray-100">
        {standings.slice(0, 4).map((team, i) => (
          <Link 
            key={team.team.id}
            to={`/clube/futebol/${team.team.id}`}
            className="p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors group"
          >
            <span className={`w-5 text-center font-bold text-xs ${i === 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
              {team.rank}
            </span>
            <img src={team.team.logo} alt="" className="w-6 h-6 object-contain group-hover:scale-110 transition-transform" />
            <span className="font-semibold text-sm flex-1 truncate group-hover:text-[#111111] transition-colors">{team.team.name}</span>
            <span className="font-mono text-sm font-bold text-gray-900">{team.points}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

// === COMPONENTE 4: WIDGET JOGOS MINI ===
const MiniMatchesWidget = ({ matches }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (!matches || matches.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
        <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
          <Calendar size={16} style={{ color: BRAND_GREEN }} />
          {t.currentRound}
        </h3>
        <button 
          className="text-xs font-bold hover:underline"
          style={{ color: BRAND_GREEN }}
        >
          {t.viewTable}
        </button>
      </div>
      <div className="divide-y divide-gray-100">
        {matches.slice(0, 3).map((match, i) => (
          <div key={i} className="p-3 hover:bg-gray-50 transition-colors">
            <div className="flex justify-between items-center text-xs mb-2 text-gray-500">
              <span>{new Date(match.date).toLocaleDateString(t.dateLocale, {weekday: 'short'})}</span>
              <span className={match.status?.includes('Live') ? 'text-red-500 font-bold' : ''}>
                {match.status || t.scheduled}
              </span>
            </div>
            <div className="flex justify-between items-center gap-2">
              <div className="flex-1 flex items-center justify-end gap-2">
                <Link 
                  to={`/clube/futebol/${match.homeTeam?.id || match.home_id}`}
                  className="font-bold text-xs truncate hover:text-[#111111] transition-colors"
                >
                  {match.homeTeam?.name || match.home_team}
                </Link>
                <Link to={`/clube/futebol/${match.homeTeam?.id || match.home_id}`}>
                  <img src={match.homeTeam?.logo || match.home_logo} className="w-5 h-5 object-contain hover:scale-110 transition-transform" alt="" />
                </Link>
              </div>
              <div className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono font-bold">
                {match.home_score ?? 0} - {match.away_score ?? 0}
              </div>
              <div className="flex-1 flex items-center justify-start gap-2">
                <Link to={`/clube/futebol/${match.awayTeam?.id || match.away_id}`}>
                  <img src={match.awayTeam?.logo || match.away_logo} className="w-5 h-5 object-contain hover:scale-110 transition-transform" alt="" />
                </Link>
                <Link 
                  to={`/clube/futebol/${match.awayTeam?.id || match.away_id}`}
                  className="font-bold text-xs truncate hover:text-[#111111] transition-colors"
                >
                  {match.awayTeam?.name || match.away_team}
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// === COMPONENTE 5: WIDGET ARTILHEIROS ===
const TopScorersWidget = ({ topScorers }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (!topScorers || topScorers.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
        <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
          <Target size={16} style={{ color: BRAND_GOLD }} />
          {t.topScorers}
        </h3>
      </div>
      <div className="divide-y divide-gray-100">
        {topScorers.slice(0, 5).map((player, i) => (
          <div key={i} className="p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
            <span className={`w-5 text-center font-bold text-xs ${i === 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
              {i + 1}
            </span>
            <div className="flex-1">
              <p className="font-semibold text-sm">{player.player_name}</p>
              <p className="text-xs text-gray-500">{player.team_name}</p>
            </div>
            <div className="text-right">
              <span className="font-bold text-sm" style={{ color: BRAND_GREEN }}>{player.goals}</span>
              <p className="text-xs text-gray-500">{t.goals}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// === COMPONENTE 6: TABELA CLASSIFICAÇÃO COMPLETA ===
const FullStandingsTable = ({ standings }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (!standings || standings.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <p className="text-gray-500">{t.noStandings}</p>
      </div>
    );
  }

  const getPositionColor = (rank) => {
    if (rank <= 4) return 'border-l-4 border-blue-500 bg-blue-50';
    if (rank <= 6) return 'border-l-4 border-green-500 bg-green-50';
    if (rank >= standings.length - 3) return 'border-l-4 border-red-500 bg-red-50';
    return '';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left p-3 text-xs font-bold text-gray-600 uppercase">{t.colPos}</th>
              <th className="text-left p-3 text-xs font-bold text-gray-600 uppercase">{t.colTeam}</th>
              <th className="text-center p-3 text-xs font-bold text-gray-600 uppercase">{t.colP}</th>
              <th className="text-center p-3 text-xs font-bold text-gray-600 uppercase">{t.colW}</th>
              <th className="text-center p-3 text-xs font-bold text-gray-600 uppercase">{t.colD}</th>
              <th className="text-center p-3 text-xs font-bold text-gray-600 uppercase">{t.colL}</th>
              <th className="text-center p-3 text-xs font-bold text-gray-600 uppercase">{t.colGD}</th>
              <th className="text-center p-3 text-xs font-bold text-gray-600 uppercase">{t.colPts}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {standings.map((team) => (
              <tr key={team.team.id} className={`hover:bg-gray-50 transition-colors ${getPositionColor(team.rank)}`}>
                <td className="p-3">
                  <span className="font-bold text-sm">{team.rank}</span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={team.team.logo} alt={team.team.name} className="w-8 h-8 object-contain" />
                    <span className="font-semibold text-sm">{team.team.name}</span>
                  </div>
                </td>
                <td className="text-center p-3 text-sm text-gray-700">{team.played}</td>
                <td className="text-center p-3 text-sm text-gray-700">{team.wins}</td>
                <td className="text-center p-3 text-sm text-gray-700">{team.draws}</td>
                <td className="text-center p-3 text-sm text-gray-700">{team.losses}</td>
                <td className="text-center p-3 text-sm font-semibold" 
                    style={{ color: team.goals_diff >= 0 ? BRAND_GREEN : '#dc2626' }}>
                  {team.goals_diff >= 0 ? '+' : ''}{team.goals_diff}
                </td>
                <td className="text-center p-3">
                  <span className="font-bold text-lg" style={{ color: BRAND_GREEN }}>
                    {team.points}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// === PÁGINA PRINCIPAL ===
export default function SocialChampionshipPage() {
  const { leagueId, season } = useParams();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  const [activeTab, setActiveTab] = useState('feed');
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [loading, setLoading] = useState(true);

  const leagueInfo = MAIN_LEAGUES.find(l => l.id === parseInt(leagueId)) || MAIN_LEAGUES[0];
  const meta = getLeagueMeta(leagueId);

  // Posts do feed (mock - em produção viriam do backend)
  const feedPosts = [
    {
      author: "Carlos Eduardo",
      time: "2h atrás",
      tag: "Opinião",
      content: `Essa briga pelo G4 no ${leagueInfo.name} está insana! O que vocês acham da disputa este ano? Quem fica de fora?`,
      likes: 45,
      comments: 12
    },
    {
      author: "Arena Stats Bot",
      time: "4h atrás",
      tag: "Estatística",
      content: "📊 Curiosidade: Esta é a temporada com maior média de gols por jogo nos últimos 10 anos (2.8 gols/jogo).",
      likes: 120,
      comments: 34
    },
    {
      author: "Maria Silva",
      time: "6h atrás",
      tag: "Discussão",
      content: "Alguém mais acha que a arbitragem está prejudicando alguns times? Vamos debater com respeito! 🧐",
      likes: 78,
      comments: 56
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [standingsRes, matchesRes, scorersRes] = await Promise.all([
          getLeagueStandings({ leagueId: parseInt(leagueId), season: parseInt(season) }),
          getLeagueRound({ leagueId: parseInt(leagueId), season: parseInt(season), round: 1 }),
          getTopPlayerStats({ leagueIds: leagueId, season: parseInt(season), limit: 10 }).catch(() => ({ data: [] }))
        ]);

        setStandings(standingsRes?.data?.groups?.[0]?.standings || []);
        setMatches(matchesRes?.data?.matches || []);
        setTopScorers(scorersRes?.data || []);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [leagueId, season]);

  return (
    <div className="min-h-screen pb-12 font-sans" style={{ backgroundColor: BG_SOCIAL }}>
      
      {/* 1. Header Fixo de Navegação */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-500">
          <Link to="/campeonatos" className="hover:text-gray-900 font-medium">{t.breadcrumb}</Link> 
          <span>/</span>
          <span className="font-bold text-gray-900">{leagueInfo.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* 2. Capa da Comunidade */}
        <LeagueCommunityHeader league={leagueInfo} season={season} meta={meta} />

        {/* 3. Navegação Interna (Abas) */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mb-6 border-b border-gray-200/50">
          {[
            { id: 'feed', label: t.tabs.feed, icon: MessageSquare },
            { id: 'standings', label: t.tabs.standings, icon: Trophy },
            { id: 'matches', label: t.tabs.matches, icon: Calendar },
            { id: 'stats', label: t.tabs.stats, icon: BarChart2 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-white shadow-sm ring-1 ring-gray-200' 
                  : 'text-gray-600 hover:bg-white/50'
              }`}
              style={activeTab === tab.id ? { color: BRAND_GREEN } : {}}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* 4. Layout Principal (Grid 8/4) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* COLUNA ESQUERDA (PRINCIPAL) */}
          <div className="lg:col-span-8 space-y-6">
             
            {activeTab === 'feed' && (
              <>
                {/* Criar Post */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                       style={{ backgroundColor: BRAND_GREEN }}>
                    EU
                  </div>
                  <input 
                    type="text" 
                    placeholder={`O que você acha do ${leagueInfo.name} este ano?`} 
                    className="flex-1 bg-gray-50 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 transition-all hover:bg-gray-100"
                    style={{ '--tw-ring-color': `${BRAND_GREEN}40` }}
                  />
                </div>

                {/* Feed Posts */}
                {loading ? (
                  <div className="space-y-4">
                    {[1,2,3].map(i => (
                      <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 animate-pulse">
                        <div className="h-20 bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  feedPosts.map((post, i) => (
                    <LeaguePost key={i} {...post} />
                  ))
                )}
              </>
            )}
             
            {activeTab === 'standings' && (
              <FullStandingsTable standings={standings} />
            )}

            {activeTab === 'matches' && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.calendarTitle}</h3>
                <p className="text-gray-500">{t.calendarSubtitle}</p>
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
                <BarChart2 size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.statsTitle}</h3>
                <p className="text-gray-500">{t.statsSubtitle}</p>
              </div>
            )}

          </div>

          {/* COLUNA DIREITA (WIDGETS) */}
          <div className="lg:col-span-4 space-y-6">
             
            {/* Widgets Contextuais */}
            {!loading && (
              <>
                <MiniStandingsWidget standings={standings} leagueId={leagueId} />
                <MiniMatchesWidget matches={matches} />
                <TopScorersWidget topScorers={topScorers} />
              </>
            )}

            {/* Widget Convite/Gamificação */}
            <div className="rounded-xl p-5 text-white shadow-lg"
                 style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #0b0b0c 100%)` }}>
              <h3 className="font-bold text-lg mb-1">{t.bolaoTitle}</h3>
              <p className="text-xs text-green-100 mb-4 opacity-90">
                {t.bolaoText}
              </p>
              <button className="w-full py-2 bg-white rounded-lg text-xs font-bold hover:bg-gray-100 transition-colors"
                      style={{ color: BRAND_GREEN }}>
                {t.bolaoBtn}
              </button>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
